import { browserTracingIntegration, createReduxEnhancer, init, logger } from "@sentry/react";

import { isDevelopmentEnv } from "@eroad/helpers/isDevelopmentEnv";
import { isString } from "@eroad/helpers/isString";
import { isTestRunEnv } from "@eroad/helpers/isTestRunEnv";

import { beforeSend } from "./helpers";
import type { CaptureLogParams, SentryInitConfig } from "./types";

/**
 * Captures a log message with the specified level and attributes.
 *
 * @example
 * ```ts
 * captureLog("This is a debug message"); // send a simple info log
 *
 * captureLog({
 *   attributes: { userId: "12345", "action": "login" },
 *   level: "warn",
 *   message: "User logged in",
 * });
 * ```
 */
function captureLog(message: string): void;
function captureLog(params: CaptureLogParams): void;
function captureLog(params: CaptureLogParams | string): void {
  if (!isSentryEnabled()) {
    return;
  }

  if (isString(params)) {
    logger.info(params);

    return;
  }

  const { attributes, level = "info", message } = params;

  switch (level) {
    case "info":
      logger.info(message, attributes);
      break;
    case "warn":
      logger.warn(message, attributes);
      break;
  }
}

/**
 * Creates a Redux enhancer for Sentry.
 * This enhancer is used to integrate Sentry with Redux for better error tracking.
 */
function getSentryReduxEnhancer(): typeof createReduxEnhancer | undefined {
  if (!isSentryEnabled()) {
    return undefined;
  }

  return createReduxEnhancer;
}

/**
 * Initializes Sentry with the provided configuration.
 */
function initSentry({ dsn, tracesSampler, tracesSampleRate }: SentryInitConfig): void {
  if (!isSentryEnabled()) {
    return;
  }

  init({
    _experiments: { enableLogs: true },
    beforeSend,
    dsn,
    environment: process.env.REACT_APP_ENVIRONMENT,
    integrations: [
      browserTracingIntegration({
        beforeStartSpan: (options) => {
          return {
            ...options,
            name: options.name.replace(
              /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i,
              ":guid",
            ),
          };
        },
      }),
    ],
    release: process.env.SENTRY_RELEASE_VERSION,
    tracesSampler,
    tracesSampleRate,
  });
}

/**
 * @internal
 */
function isSentryEnabled(): boolean {
  if (isDevelopmentEnv() || isTestRunEnv()) {
    return false;
  }

  return true;
}

export { captureLog, getSentryReduxEnhancer, initSentry };
export { captureException, createReduxEnhancer, ErrorBoundary } from "@sentry/react";
